import { useForm } from "@tanstack/react-form";

import {
  useAutosave,
  type UseAutosaveOptions,
} from "@/lib/hooks/useAutosave";

/**
 * A TanStack Form with autosave wired in by default.
 *
 * Every value change triggers the debounced autosave (debounce + abort + beacon
 * flush + visible status all come from useAutosave), so on-change persistence
 * is the default for forms built with the kit — not something each form
 * re-implements. Read `autosave.status` to render <SaveStatus />.
 *
 * The form-level `listeners.onChange` fires immediately on each change;
 * useAutosave owns the debounce, so we don't double-debounce here.
 */
export function useAutosaveForm<TValues>(params: {
  defaultValues: TValues;
  autosave: UseAutosaveOptions<TValues>;
  onSubmit?: (value: TValues) => void | Promise<void>;
}) {
  const autosave = useAutosave<TValues>(params.autosave);

  const form = useForm({
    defaultValues: params.defaultValues,
    listeners: {
      onChange: ({ formApi }) => {
        autosave.save(formApi.state.values as TValues);
      },
    },
    onSubmit: async ({ value }) => {
      await params.onSubmit?.(value as TValues);
    },
  });

  return { form, autosave };
}
